import type { RedoMerchantRpcClient } from "@redotech/merchant-sdk-client/client";
import type { CustomAnalyticsReportSummary } from "@redotech/merchant-sdk/rpc/schema/analytics/reports";
import { useRequiredContext } from "@redotech/react-util/context";
import type { CustomAnalyticsReportSort } from "@redotech/redo-analytics-common/reports/custom-analytics-report";
import type { CustomAnalyticsReportColumnDraft } from "@redotech/redo-analytics-common/reports/custom-analytics-report-column-catalog";
import type { CustomAnalyticsReportFilterDraft } from "@redotech/redo-analytics-common/reports/custom-analytics-report-filter-editor";
import type { CustomAnalyticsReportFilter } from "@redotech/redo-analytics-common/reports/custom-analytics-report-filters";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import {
  CancelledError,
  infiniteQueryOptions,
  type QueryClient,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { getCustomAnalyticsReportQueryKey } from "./use-custom-analytics-report-query";
import { getCustomAnalyticsReportStructureQueryKey } from "./use-custom-analytics-report-structure-query";

const CUSTOM_ANALYTICS_REPORT_ROWS_PAGE_SIZE = 25;
const CUSTOM_ANALYTICS_REPORT_ROWS_QUERY_KEY = "customAnalyticsReportRows";

export function getCustomAnalyticsReportRowsQueryKey(
  teamId: string,
  customReportId: string,
  timezone?: string,
  sorts?: readonly CustomAnalyticsReportSort[],
  filters?: readonly CustomAnalyticsReportFilter[],
  expectedUpdatedAt?: Date,
  columnDraft?: CustomAnalyticsReportColumnDraft,
  filterDraft?: CustomAnalyticsReportFilterDraft,
) {
  return [
    CUSTOM_ANALYTICS_REPORT_ROWS_QUERY_KEY,
    teamId,
    customReportId,
    timezone ?? null,
    sorts ?? null,
    filters ?? [],
    expectedUpdatedAt?.toISOString() ?? null,
    columnDraft ?? null,
    filterDraft ?? null,
  ] as const;
}

export function getCustomAnalyticsReportRowsQueryInvalidationKey(
  teamId: string,
  customReportId?: string,
) {
  return customReportId
    ? ([
        CUSTOM_ANALYTICS_REPORT_ROWS_QUERY_KEY,
        teamId,
        customReportId,
      ] as const)
    : ([CUSTOM_ANALYTICS_REPORT_ROWS_QUERY_KEY, teamId] as const);
}

interface CustomAnalyticsReportRowsQueryParams {
  customReportId: string;
  columnDraft?: CustomAnalyticsReportColumnDraft;
  filterDraft?: CustomAnalyticsReportFilterDraft;
  expectedUpdatedAt?: Date;
  timezone?: string;
  sorts?: readonly CustomAnalyticsReportSort[];
  filters?: readonly CustomAnalyticsReportFilter[];
  enabled?: boolean;
}

export function useCustomAnalyticsReportRowsQuery(
  params: CustomAnalyticsReportRowsQueryParams,
) {
  const team = useRequiredContext(TeamContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const queryClient = useQueryClient();
  return useInfiniteQuery(
    customAnalyticsReportRowsQueryOptions({
      ...params,
      teamId: team._id,
      rpcClient,
      queryClient,
    }),
  );
}

export function customAnalyticsReportRowsQueryOptions({
  customReportId,
  columnDraft,
  filterDraft,
  expectedUpdatedAt,
  timezone,
  sorts,
  filters = [],
  enabled = true,
  teamId,
  rpcClient,
  queryClient,
}: CustomAnalyticsReportRowsQueryParams & {
  teamId: string;
  rpcClient: Pick<
    RedoMerchantRpcClient,
    "getCustomAnalyticsReportRows" | "getCustomAnalyticsReportDraftRows"
  >;
  queryClient: QueryClient;
}) {
  return infiniteQueryOptions({
    queryKey: getCustomAnalyticsReportRowsQueryKey(
      teamId,
      customReportId,
      timezone,
      sorts,
      filters,
      expectedUpdatedAt,
      columnDraft,
      filterDraft,
    ),
    queryFn: async ({ pageParam, signal }) => {
      try {
        const request = {
          id: customReportId,
          columnDraft,
          expectedUpdatedAt,
          filterDraft,
          includeCreator: false,
          sorts: sorts ? [...sorts] : undefined,
          filters: [...filters],
          timezone,
          offset: pageParam,
          limit: CUSTOM_ANALYTICS_REPORT_ROWS_PAGE_SIZE,
        };
        const page = filterDraft
          ? await rpcClient.getCustomAnalyticsReportDraftRows(
              { ...request, filterDraft },
              { signal },
            )
          : await rpcClient.getCustomAnalyticsReportRows(request, { signal });
        if (
          expectedUpdatedAt &&
          page.report.updatedAt.getTime() !== expectedUpdatedAt.getTime()
        ) {
          throw new Error("The report changed. Refresh and try again.");
        }
        if (
          columnDraft &&
          (page.columns.length !== columnDraft.definition.columns.length ||
            page.columns.some(
              (column, index) =>
                column.sourceName !==
                columnDraft.definition.columnPresentation[index]?.sourceName,
            ))
        ) {
          throw new Error(
            "The column preview is unavailable. Refresh and try again.",
          );
        }
        if (signal.aborted) {
          throw new CancelledError();
        }
        if (!columnDraft && !filterDraft) {
          const reportQueryKey = getCustomAnalyticsReportQueryKey(
            teamId,
            customReportId,
          );
          let refreshReport = false;
          queryClient.setQueryData<CustomAnalyticsReportSummary>(
            reportQueryKey,
            (current) => {
              if (current && current.updatedAt > page.report.updatedAt) {
                return current;
              }
              if (
                current?.filterDefinition &&
                !page.report.filterDefinition &&
                current.updatedAt < page.report.updatedAt
              ) {
                refreshReport = true;
                return current;
              }
              return {
                ...page.report,
                filterDefinition:
                  page.report.filterDefinition ??
                  (current?.updatedAt.getTime() ===
                  page.report.updatedAt.getTime()
                    ? current.filterDefinition
                    : undefined),
              };
            },
          );
          if (refreshReport) {
            void queryClient.invalidateQueries({
              queryKey: reportQueryKey,
              exact: true,
            });
          }
        }
        return page;
      } catch (error) {
        if (!signal.aborted) {
          void queryClient.invalidateQueries({
            queryKey: getCustomAnalyticsReportStructureQueryKey(
              teamId,
              customReportId,
            ),
          });
        }
        throw error;
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset ?? undefined,
    staleTime: 10 * 1000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    enabled,
  });
}
